import { useCallback } from 'react';
import {
  AccountManager,
  ACCOUNT_MANAGER_BULK_TOO_LARGE,
  ACCOUNT_MANAGER_COUNT_CHANGED,
  ACCOUNT_MANAGER_FORBIDDEN,
  ACCOUNT_MANAGER_MEMBER_NOT_FOUND,
  ACCOUNT_MANAGER_NOT_ACTIVE,
  ACCOUNT_MANAGER_OPERATOR_UNVERIFIED,
  ACCOUNT_MANAGER_UNAVAILABLE,
  getAccountManagerRefusal,
} from '../../api/account-managers';
import { ASSIGN_MATCHING_MAX } from '../../api/customer';
import type { OperatorIdentity } from '../../api/operatorHeaders';
import { useOperatorIdentity } from '../../hooks/useOperatorIdentity';
import { useOperatorToken } from '../../hooks/useOperatorToken';
import {
  customerInformationStrings,
  formatMessage,
} from './CustomerInformation.strings';

/**
 * Shared by the member list and the member detail: manager names by key, the
 * operator-facing text of a refused assignment, and the operator identity
 * sent on each write (abo-account-manager-assignment task 04).
 */
const S = customerInformationStrings.accountManager;

/** The roster as the member pages use it. */
export interface AccountManagerRoster {
  /** Active managers, in DS order: the choices of every select. */
  active: AccountManager[];
  /** Every manager (active and inactive) by key, for name resolution. */
  byKey: Record<string, AccountManager>;
}

export const EMPTY_ACCOUNT_MANAGER_ROSTER: AccountManagerRoster = { active: [], byKey: {} };

/**
 * Builds the roster from every manager (active and inactive). `key` is the
 * DS alias of `hubspotId`; an older DS sends only the latter, so it fills in.
 */
export const toAccountManagerRoster = (managers: AccountManager[]): AccountManagerRoster => {
  const keyed = managers
    .map((manager) => ({ ...manager, key: manager.key || manager.hubspotId }))
    .filter((manager) => Boolean(manager.key));
  return {
    active: keyed.filter((manager) => manager.isActive !== false),
    byKey: keyed.reduce<Record<string, AccountManager>>((acc, manager) => {
      acc[manager.key] = manager;
      return acc;
    }, {}),
  };
};

/** "First Last", or "First Last (inactive)" for a deactivated manager. */
export const accountManagerDisplayName = (manager: AccountManager): string => {
  const name = `${manager.firstName ?? ''} ${manager.lastName ?? ''}`.trim();
  return manager.isActive === false ? formatMessage(S.inactiveName, { name }) : name;
};

/** A member's manager name; "-" when the key is missing or matches no manager. */
export const accountManagerName = (
  key: string | null | undefined,
  roster: AccountManagerRoster,
): string => {
  const manager = key ? roster.byKey[key] : undefined;
  return manager ? accountManagerDisplayName(manager) : S.unknown;
};

/** The operator-facing text of a failed assignment write. */
export const assignmentFailureMessage = (error: unknown): string => {
  switch (getAccountManagerRefusal(error)?.code) {
    case ACCOUNT_MANAGER_OPERATOR_UNVERIFIED:
      return S.refusal.unverified;
    case ACCOUNT_MANAGER_FORBIDDEN:
      return S.refusal.forbidden;
    case ACCOUNT_MANAGER_UNAVAILABLE:
      return S.refusal.unavailable;
    case ACCOUNT_MANAGER_NOT_ACTIVE:
      return S.refusal.notActive;
    case ACCOUNT_MANAGER_MEMBER_NOT_FOUND:
      return S.refusal.memberNotFound;
    case ACCOUNT_MANAGER_COUNT_CHANGED:
      return S.bulk.countChanged;
    case ACCOUNT_MANAGER_BULK_TOO_LARGE:
      return formatMessage(S.bulk.tooLarge, { max: ASSIGN_MATCHING_MAX.toLocaleString('en-US') });
    default:
      return S.refusal.generic;
  }
};

/**
 * The operator for ONE assignment write: the e-mail plus a freshly renewed
 * FusionAuth id token (never stored).
 */
export const useOperatorForWrite = (): (() => Promise<OperatorIdentity>) => {
  const readOperator = useOperatorIdentity();
  const { getOperatorToken } = useOperatorToken();
  return useCallback(
    async () => ({ ...readOperator(), token: await getOperatorToken() }),
    [readOperator, getOperatorToken],
  );
};
